/**
 * Fetching a case's report template through the authenticated proxy (MDRS-503).
 *
 * `CaseSchema.report_template_url` used to carry an 8-hour Azure SAS URL, minted
 * on every case read — including the *list*, which handed out one signed link
 * per row on every page load. The field is gone; the template's bytes now come
 * from `GET /cases/{case_id}/report-template`, which re-checks ownership on
 * each fetch.
 *
 * `report_template_path` is still on the payload, so callers can tell whether a
 * template exists (and show its filename) without fetching anything.
 */
import { fetchBlobObjectUrl } from "../../api/blobFetch";

const DOCX_MIME_TYPE =
  "application/vnd.openxmlformats-officedocument.wordprocessingml.document";

/** Path to the case's report-template proxy endpoint. */
export function caseReportTemplateUrl(caseId: string): string {
  return `/api/v1/cases/${encodeURIComponent(caseId)}/report-template`;
}

/**
 * Fetch the case's report template and return an object URL for the viewer.
 *
 * The caller owns the returned URL and should revoke it when the preview closes.
 */
export async function fetchCaseTemplatePreviewUrl(
  caseId: string | undefined
): Promise<string> {
  if (!caseId) {
    throw new Error("Failed to fetch case template: missing case id");
  }
  return fetchBlobObjectUrl(caseReportTemplateUrl(caseId), DOCX_MIME_TYPE);
}
